export type Observer<T> = (data: T) => void;

export interface WatchSubjectConfig {
  notifyOnSubscribe?: boolean;
}

/**
 * A class that can notify observers of changes.
 *
 * @example
 * ```ts
 * // Class with an observable property
 * class Test extends SimpleObservableObservable {
 *   public click = new SimpleObservableObservableSubject<string>();
 *
 *   public onClick() {
 *     this.notify(this.click, "test");
 *   }
 * }
 *
 * // Then on the observer:
 * const test = new Test();
 * test.click.subscribe((data) => {
 *   console.log(data); // "test"
 * });
 *
 */
export abstract class SimpleObservable {
  protected notify<T>(subject: SimpleObservableSubject<T>, data: T) {
    subject[notifyObservableSymbol](data);
  }
}

export function notifySubject<T>(subject: SimpleObservableSubject<T>, data: T) {
  subject[notifyObservableSymbol](data);
}

/**
 * Makes the notify method for `SimpleObservableSubject` private to this module.
 */
const notifyObservableSymbol = Symbol("notifyObservable");

/**
 * A subject that can be observed by multiple observers.
 *
 * Use in conjunction with the `SimpleObservable` class.
 */
export class SimpleObservableSubject<T> {
  protected observers: Set<Observer<T>>;
  protected canHoldState = true;

  lastState: { data: T } | null = null;

  constructor(
    options: {
      observers?: Set<Observer<T>>;
      canHoldState?: boolean;
    } = {},
  ) {
    const { observers = new Set(), canHoldState = true } = options;
    this.observers = observers;
    this.canHoldState = canHoldState;
  }

  subscribe(
    observer: Observer<T>,
    { notifyOnSubscribe = true }: WatchSubjectConfig = {},
  ) {
    this.observers.add(observer);

    if (notifyOnSubscribe && this.lastState) {
      observer(this.lastState.data);
    }

    return () => this.unsubscribe(observer);
  }

  unsubscribe(observer: Observer<T>) {
    this.observers.delete(observer);
  }

  [notifyObservableSymbol](data: T) {
    this.lastState = { data };

    for (const observer of this.observers) {
      try {
        observer(data);
      } catch (error) {
        console.error(error);
      }
    }
  }
}
